import { Link } from '@inertiajs/react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Charity } from '../../Shared/types';
import SupportaSymbol from '../../Shared/UI/SupportaSymbol';
import { useRouter } from '../router';
import { NavItem } from '../types';
import Container from './Container';
import { EmbedContext } from './EmbedProvider';
import { LocaleSelector } from './LocaleSelector';

export interface FooterLink {
  title: string;
  url: string;
}

export function Footer({
  links,
  charity,
}: {
  links?: NavItem[];
  charity?: Charity | null,
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { isEmbedded } = useContext(EmbedContext);

  const linkProps = isEmbedded ? {
    target: '_blank',
    rel: 'noopener noreferrer',
  } : {};

  const LinkComponent = isEmbedded ? 'a' : Link;

  return (
    <>
      {links && (
        <div className="border-t border-slate-150 text-sm bg-slate-50">
          <Container size="lg" spacing="lg" className="leading-loose">
            <div className="grid sm:grid-cols-3 gap-4 sm:gap-8 text-center sm:text-left">
              <div>
                <h5 className="text-slate-500 font-bold mb-1">
                  {t('frontend:homepage.supporta')}
                </h5>
                <ul>
                  <li>
                    <LinkComponent href={routes.home_page()} {...linkProps} className="text-slate-700">
                      {t('frontend:homepage.campaigns')}
                    </LinkComponent>
                  </li>
                  <li>
                    <LinkComponent href={routes.charities_page()} {...linkProps} className="text-slate-700">
                      {t('frontend:homepage.charities')}
                    </LinkComponent>
                  </li>
                  <li>
                    <LinkComponent href={routes.events_page()} {...linkProps} className="text-slate-700">
                      {t('frontend:homepage.events')}
                    </LinkComponent>
                  </li>
                  <li>
                    <LinkComponent href={routes.select_charity_page()} {...linkProps} className="text-slate-700">
                      {t('frontend:start_campaign')}
                    </LinkComponent>
                  </li>
                </ul>
              </div>
              {links.length > 0 && (
                <div>
                  <h5 className="text-slate-500 font-bold mb-1">
                    {t('frontend:homepage.about')}
                  </h5>
                  <ul>
                    {links.map((link) => link.url && (
                      <li key={link.id}>
                        <a
                          href={link.url}
                          target={link.target}
                          rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                          className="text-slate-700"
                          key={link.id}
                        >
                          {link.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <h5 className="text-slate-500 font-bold mb-1">
                  {t('frontend:homepage.other')}
                </h5>
                <ul>
                  {charity && (
                    <li>
                      <LinkComponent href={routes.help_page()} {...linkProps} className="text-slate-700">
                        {t('frontend:help')}
                      </LinkComponent>
                    </li>
                  )}
                  <li>
                    <LinkComponent href={charity ? routes.privacy_page() : routes.content_privacy_page()} {...linkProps} className="text-slate-700">
                      {t('frontend:privacy_statement')}
                    </LinkComponent>
                  </li>
                  <li>
                    <LinkComponent href={routes.content_terms_page()} {...linkProps} className="text-slate-700">
                      {t('frontend:terms_of_service')}
                    </LinkComponent>
                  </li>
                </ul>
              </div>
            </div>
          </Container>
        </div>
      )}
      <div className="border-t border-slate-150">
        <Container size="lg" spacing="md" className="text-sm text-center sm:text-left">
          <div className="sm:flex items-center justify-between">
            <ul className="flex flex-col space-y-3 mb-6 sm:items-center sm:flex-row sm:space-y-0 sm:space-x-5 sm:mb-0">
              <li>
                <a
                  href="https://supporta.com"
                  rel="noopener noreferrer"
                  title="Supporta"
                  {...linkProps}
                  className="text-slate-300 hover:text-emerald-500 transition-colors"
                >
                  <SupportaSymbol className="w-4 inline-block" />
                </a>
              </li>
              {!links && (
                <>
                  {charity && (
                    <li>
                      <LinkComponent href={routes.help_page()} {...linkProps} className="text-slate-500">
                        {t('frontend:help')}
                      </LinkComponent>
                    </li>
                  )}
                  <li>
                    <LinkComponent href={charity ? routes.privacy_page() : routes.content_privacy_page()} {...linkProps} className="text-slate-500">
                      {t('frontend:privacy_statement')}
                    </LinkComponent>
                  </li>
                  <li>
                    <LinkComponent href={routes.content_terms_page()} {...linkProps} className="text-slate-500">
                      {t('frontend:terms_of_service')}
                    </LinkComponent>
                  </li>
                </>
              )}
            </ul>

            <LocaleSelector />
          </div>
        </Container>
      </div>
    </>
  );
}
